/**
 * Carrom Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * and multiplayer stats.
 *
 * Follows LudoStatisticsService pattern.
 */

import {
  CarromStatistics,
  CarromGameResult,
  CarromGameStats,
  CarromDifficulty,
  CarromGameSettings,
  CarromPlayer,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class CarromStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'carrom_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): CarromStatistics {
    return {
      singlePlayer: {
        easy: this.createEmptyGameStats(),
        medium: this.createEmptyGameStats(),
        hard: this.createEmptyGameStats(),
      },
      localMultiplayer: this.createEmptyGameStats(),
      onlineMultiplayer: this.createEmptyGameStats(),
      totalGames: 0,
      totalPlayTime: 0,
    };
  }

  private createEmptyGameStats(): CarromGameStats {
    return {
      wins: 0,
      losses: 0,
      draws: 0,
      winPercentage: 0,
      averageScore: 0,
      totalGames: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): CarromStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as CarromStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get carrom statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(
    result: CarromGameResult,
    playerIdentity: CarromPlayer,
    userId?: string
  ): boolean {
    try {
      const stats = this.getStatistics(userId);

      if (result.mode === 'singleplayer' && result.difficulty) {
        this.updateGameStats(stats.singlePlayer[result.difficulty], result, playerIdentity);
      } else if (result.mode === 'local-multiplayer') {
        this.updateGameStats(stats.localMultiplayer, result, playerIdentity);
      } else if (result.mode === 'multiplayer-realtime') {
        this.updateGameStats(stats.onlineMultiplayer, result, playerIdentity);
      }

      stats.totalGames++;
      stats.totalPlayTime += result.duration;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record carrom game result:', error);
      return false;
    }
  }

  private updateGameStats(
    stats: CarromGameStats,
    result: CarromGameResult,
    playerIdentity: CarromPlayer
  ): void {
    stats.totalGames++;

    if (!result.winner) {
      stats.draws++;
    } else if (result.winner === playerIdentity) {
      stats.wins++;
    } else {
      stats.losses++;
    }

    stats.winPercentage =
      stats.totalGames > 0 ? (stats.wins / stats.totalGames) * 100 : 0;

    // Update average score
    const playerScore =
      playerIdentity === 'player1' ? result.player1Score : result.player2Score;
    stats.averageScore =
      stats.totalGames > 0
        ? (stats.averageScore * (stats.totalGames - 1) + playerScore) / stats.totalGames
        : playerScore;
  }

  /** Get statistics for a specific mode and difficulty. */
  getModeStatistics(
    mode: CarromGameSettings['mode'],
    difficulty?: CarromDifficulty,
    userId?: string
  ): CarromGameStats {
    const stats = this.getStatistics(userId);

    if (mode === 'singleplayer' && difficulty) {
      return stats.singlePlayer[difficulty];
    } else if (mode === 'local-multiplayer') {
      return stats.localMultiplayer;
    } else if (mode === 'multiplayer-realtime') {
      return stats.onlineMultiplayer;
    }

    return this.createEmptyGameStats();
  }

  /** Reset all statistics. */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset carrom statistics:', error);
      return false;
    }
  }
}
